import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ReportCVModalProps {
  isOpen: boolean;
  onClose: () => void;
  cvId: string;
  onSubmitSuccess: () => void;
  apiCall: (url: string, options: RequestInit) => Promise<Response>;
}

interface ReportData {
  cv_id: string;
  reason: string;
  description: string;
}

const ReportCVModal: React.FC<ReportCVModalProps> = ({ 
  isOpen, 
  onClose, 
  cvId,
  onSubmitSuccess,
  apiCall 
}) => {
  const { t } = useTranslation('common');
  const [reportData, setReportData] = useState<ReportData>({
    cv_id: cvId,
    reason: 'inappropriate',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reportData.cv_id || !reportData.reason) {
      setError(t('explore.reportModal.errorRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await apiCall(`${apiBaseUrl}/api/v1/moderation/report-cv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      });

      const result = await response.json();

      if (result.success) {
        onSubmitSuccess();
        onClose();
        setReportData({
          cv_id: cvId,
          reason: 'inappropriate',
          description: ''
        });
      } else {
        setError(result.message || t('explore.reportModal.errorSubmitFailed'));
      }
    } catch (err: any) {
      console.error('Error submitting CV report:', err);
      setError(t('explore.reportModal.errorSubmitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setReportData({
        cv_id: cvId,
        reason: 'inappropriate',
        description: ''
      });
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[10001] flex items-end sm:items-center justify-center sm:p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl sm:max-w-lg w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle (mobile) */}
              <div className="flex justify-center pt-2 pb-0 sm:hidden">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 sm:p-5 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-sm sm:text-lg font-bold text-gray-900">{t('explore.reportModal.title')}</h2>
                    <p className="text-[11px] sm:text-sm text-gray-500">{t('explore.reportModal.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="px-4 py-3 sm:p-5 space-y-3 sm:space-y-4">
                {/* Reason */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('explore.reportModal.reasonLabel')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={reportData.reason}
                    onChange={(e) => setReportData({ ...reportData, reason: e.target.value })}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <option value="inappropriate">{t('explore.reportModal.reasonInappropriate')}</option>
                    <option value="sensitive_information">{t('explore.reportModal.reasonSensitive')}</option>
                    <option value="fake">{t('explore.reportModal.reasonFake')}</option>
                    <option value="spam">{t('explore.reportModal.reasonSpam')}</option>
                    <option value="copyright">{t('explore.reportModal.reasonCopyright')}</option>
                    <option value="other">{t('explore.reportModal.reasonOther')}</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('explore.reportModal.descriptionLabel')}
                  </label>
                  <textarea
                    value={reportData.description}
                    onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                    disabled={isSubmitting}
                    placeholder={t('explore.reportModal.descriptionPlaceholder')}
                    rows={3}
                    maxLength={1000}
                    className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed resize-none text-sm"
                  />
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-[10px] text-gray-400">
                      {t('explore.reportModal.descriptionHint')}
                    </p>
                    <p className={`text-[10px] font-medium ${
                      reportData.description.length > 900 
                        ? 'text-red-600' 
                        : reportData.description.length > 800 
                        ? 'text-yellow-600' 
                        : 'text-gray-400'
                    }`}>
                      {reportData.description.length}/1000
                    </p>
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-2.5 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-red-600 text-xs">⚠️</span>
                      <span className="text-xs text-red-800">{error}</span>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 sm:p-4 border-t border-gray-100">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="px-3 py-2 text-xs sm:text-sm text-gray-600 font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('explore.reportModal.cancelButton')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !reportData.cv_id || !reportData.reason}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('explore.reportModal.submittingButton')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>{t('explore.reportModal.submitButton')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ReportCVModal;
